/* eslint-disable no-console */
import * as React from 'react'
import tw from 'tailwind-styled-components'

type FlexDirection = 'row' | 'col'

type Spacing = 'none' | 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge'

type ResponsiveSpacing = {
  default: Spacing
  sm?: Spacing
  md?: Spacing
  lg?: Spacing
}

type Props = {
  /** The alignment of the children */
  align?: 'start' | 'end' | 'center' | 'stretch'
  /** The direction of the flex container */
  $flexDirection?:
    | FlexDirection
    | {
        default: FlexDirection
        sm?: FlexDirection
        md?: FlexDirection
        lg?: FlexDirection
      }
  /** The horizontal spacing between children */
  $horizontalSpacing?: null | Spacing | ResponsiveSpacing
  /** The vertical spacing between children */
  justify?: null | 'start' | 'end' | 'center' | 'between' | 'around'
  /** The vertical spacing between children */
  $verticalSpacing?: null | Spacing | ResponsiveSpacing
  $wrap?: boolean

  className?: string
}

function propsToFlexDirection(props: Props): string {
  if (typeof props.$flexDirection === 'string') {
    return props.$flexDirection ? `flex-${props.$flexDirection}` : 'flex-row'
  }

  const defaultFlexDirection = props.$flexDirection?.default ?? 'row'

  return `
    flex-${props.$flexDirection?.default ?? defaultFlexDirection}
    sm:flex-${props.$flexDirection?.sm ?? defaultFlexDirection}
    md:flex-${props.$flexDirection?.md ?? defaultFlexDirection}
    lg:flex-${props.$flexDirection?.lg ?? defaultFlexDirection}
  `
}

function sizeToSpacing(size: Spacing, axis: 'x' | 'y'): string {
  switch (size) {
    case 'none':
      return ``
    case 'xsmall':
      return `gap-1`
    case 'small':
      return `gap-2`
    case 'medium':
      return `gap-4`
    case 'large':
      return `gap-8`
    case 'xlarge':
      return `gap-16`
    default:
      return `gap-0`
  }
}

function propsToSpacing(
  props: Props,
  direction: 'horizontal' | 'vertical'
): string {
  const spacingProp =
    direction === 'horizontal'
      ? props.$horizontalSpacing
      : props.$verticalSpacing

  if (!spacingProp) return ''

  const axis = direction === 'horizontal' ? 'x' : 'y'

  // Handle string spacing values
  if (typeof spacingProp === 'string') {
    // Validate that the string value is a valid Spacing type
    if (!isValidSpacing(spacingProp)) {
      console.warn(`Invalid ${direction} spacing value: "${spacingProp}"`)
      return ''
    }
    return sizeToSpacing(spacingProp, axis)
  }

  // Validate object type
  if (typeof spacingProp !== 'object' || spacingProp === null) {
    console.warn(
      `Invalid ${direction} spacing prop type provided to Flex component`
    )
    return ''
  }

  // Validate and get spacing values with type guard
  const defaultSpacing = validateSpacing(spacingProp.default, 'medium')
  const smSpacing = validateSpacing(spacingProp.sm, defaultSpacing)
  const mdSpacing = validateSpacing(spacingProp.md, defaultSpacing)
  const lgSpacing = validateSpacing(spacingProp.lg, defaultSpacing)

  return `
    ${sizeToSpacing(defaultSpacing, axis)}
    sm:${sizeToSpacing(smSpacing, axis)}
    md:${sizeToSpacing(mdSpacing, axis)}
    lg:${sizeToSpacing(lgSpacing, axis)}
  `.trim()
}

// Add helper functions for type safety
function isValidSpacing(value: unknown): value is Spacing {
  const validSpacings = ['none', 'xsmall', 'small', 'medium', 'large', 'xlarge']
  return typeof value === 'string' && validSpacings.includes(value)
}

function validateSpacing(value: unknown, fallback: Spacing): Spacing {
  if (isValidSpacing(value)) return value
  return fallback
}

const StyledFlex = tw.div<Props>`
  flex

  ${(props: Props) => propsToFlexDirection(props)}
  ${(props: Props) => propsToSpacing(props, 'horizontal')}
  ${(props: Props) => propsToSpacing(props, 'vertical')}

  ${(props: Props) => {
    if (props.justify === 'start') {
      return 'justify-start'
    } else if (props.justify === 'end') {
      return 'justify-end'
    } else if (props.justify === 'center') {
      return 'justify-center'
    } else if (props.justify === 'between') {
      return 'justify-between'
    } else if (props.justify === 'around') {
      return 'justify-around'
    } else {
      return ''
    }
  }}

  ${(props: Props) => {
    if (props.align === 'start') {
      return 'items-start'
    } else if (props.align === 'end') {
      return 'items-end'
    } else if (props.align === 'center') {
      return 'items-center'
    } else if (props.align === 'stretch') {
      return 'items-stretch'
    } else {
      return ''
    }
  }}

  ${(props: Props) => {
    if (props.$wrap) {
      return `
        flex-wrap
        gap-y-10
      `
    }

    return ''
  }}
 `

/** A flex container component */
const Flex = ({ children, ...props }: React.PropsWithChildren<Props>) => {
  const safeProps = { ...props }

  // Improved runtime validation for spacing props
  if (
    safeProps.$horizontalSpacing &&
    typeof safeProps.$horizontalSpacing !== 'string' &&
    typeof safeProps.$horizontalSpacing !== 'object'
  ) {
    console.warn('Invalid $horizontalSpacing prop type provided to Flex')
    safeProps.$horizontalSpacing = null
  }

  if (
    safeProps.$verticalSpacing &&
    typeof safeProps.$verticalSpacing !== 'string' &&
    typeof safeProps.$verticalSpacing !== 'object'
  ) {
    console.warn('Invalid $verticalSpacing prop type provided to Flex')
    safeProps.$verticalSpacing = null
  }

  return <StyledFlex {...safeProps}>{children}</StyledFlex>
}

export default Flex
